import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { PoweredByClicksMondayGis } from '@/components/ClicksMondayGisLogo';
import { useSyncStatus, type SyncRunLogEntry } from '@/hooks/useSyncStatus';
import {
  isEmbeddedFrame,
  startArcGisOAuth,
  subscribeArcGisOAuthResult,
} from '@/lib/arcgis-oauth-nav';
import type { EnrichedFeature } from '@/hooks/useGeoLayer';
import {
  countExportableFeatures,
  exportFeaturesToGeoJsonFile,
  fetchFreshGeoLayerFeatures,
} from '@/lib/geojson-export';
import { getLayerDisplayName, LAYER_SYNC_HINTS } from './layer-options';
import styles from './SyncControlPanel.module.css';

interface SyncControlPanelProps {
  layerId: string;
  features: EnrichedFeature[];
  mapLoading?: boolean;
}

function formatRelativeTime(isoDate: string | null | undefined): string {
  if (!isoDate) return 'טרם בוצע סנכרון';
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'לפני פחות מדקה';
  if (diffMin < 60) return `לפני ${diffMin} דק׳`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `לפני ${diffH} שע׳`;
  return `לפני ${Math.floor(diffH / 24)} ימים`;
}

function formatRunDateTime(isoDate: string): string {
  return new Intl.DateTimeFormat('he-IL', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(isoDate));
}

type StatusTone = 'success' | 'warn' | 'muted' | 'danger';

/** Source → destination in LTR so arrows are not mirrored in RTL layout. */
function SyncFlowLabel({ from, to }: { from: string; to: string }) {
  return (
    <span className={styles.syncFlowLabel} dir="ltr">
      {from} → {to}
    </span>
  );
}

function LastRunMetrics({ run }: { run: SyncRunLogEntry }) {
  if (!run.run_completed_at) return null;

  return (
    <dl className={styles.summaryMetrics} aria-label="תוצאות הרצה אחרונה">
      <div className={styles.summaryMetric}>
        <dt>גאומטריה</dt>
        <dd>{run.target_to_source_count}</dd>
      </div>
      <div className={styles.summaryMetric}>
        <dt>CRM</dt>
        <dd>{run.source_to_target_count}</dd>
      </div>
      <div className={`${styles.summaryMetric} ${styles.summaryMetricWide}`}>
        <dt>הרצה אחרונה</dt>
        <dd>{formatRelativeTime(run.run_completed_at)}</dd>
      </div>
    </dl>
  );
}

function computeStatus(
  isEnabled: boolean,
  arcgisConnected: boolean | null,
  lastRun: SyncRunLogEntry | null,
): { tone: StatusTone; title: string; hint: string } {
  if (arcgisConnected === false) {
    return { tone: 'muted', title: 'לא מחובר ל-ArcGIS', hint: 'התחבר כדי להפעיל סנכרון' };
  }
  if (!isEnabled) {
    return { tone: 'warn', title: 'מחובר · סנכרון אוטומטי כבוי', hint: 'הפעל את המתג או לחץ סנכרן עכשיו' };
  }
  if (!lastRun?.run_completed_at) {
    return { tone: 'warn', title: 'מחובר · ממתין להרצה ראשונה', hint: 'הפעל סנכרון ידני לבדיקה' };
  }
  if (lastRun.error_count > 0) {
    return { tone: 'danger', title: 'הסנכרון האחרון נכשל חלקית', hint: 'פתח היסטוריה לפרטי שגיאה' };
  }
  const ageMs = Date.now() - new Date(lastRun.run_completed_at).getTime();
  if (ageMs < 10 * 60 * 1000) {
    return { tone: 'success', title: 'מסונכרן', hint: 'הנתונים עדכניים' };
  }
  return { tone: 'warn', title: 'מחובר · דורש רענון', hint: 'הרצה אחרונה לפני יותר מ-10 דק׳' };
}

function RunStatusChip({ run }: { run: SyncRunLogEntry }) {
  if (!run.run_completed_at) {
    return <span className={`${styles.chip} ${styles.chipRunning}`}>רץ…</span>;
  }
  if (run.error_count > 0) {
    return <span className={`${styles.chip} ${styles.chipError}`}>שגיאות</span>;
  }
  return <span className={`${styles.chip} ${styles.chipOk}`}>הושלם</span>;
}

function HistoryRow({ run }: { run: SyncRunLogEntry }) {
  const [errorsOpen, setErrorsOpen] = useState(false);
  const geoCount = run.target_to_source_count;
  const crmCount = run.source_to_target_count;
  const hasErrors = run.error_count > 0;
  const errors = Array.isArray(run.errors_json) ? run.errors_json : [];

  return (
    <article className={styles.historyCard}>
      <div className={styles.historyCardHead}>
        <div className={styles.historyCardMeta}>
          <time className={styles.historyWhen} dateTime={run.run_started_at}>
            {formatRunDateTime(run.run_started_at)}
          </time>
          <span className={styles.historyTrigger}>
            {run.triggered_by === 'manual' ? 'הפעלה ידנית' : 'אוטומטי'}
          </span>
        </div>
        <RunStatusChip run={run} />
      </div>

      <ul className={styles.historyStats}>
        <li className={styles.historyStatRow}>
          <span className={styles.historyStatLabel}>
            גאומטריה · <SyncFlowLabel from="ArcGIS" to="Monday" />
          </span>
          <span className={styles.historyStatCount}>{geoCount}</span>
        </li>
        <li className={styles.historyStatRow}>
          <span className={styles.historyStatLabel}>
            CRM · <SyncFlowLabel from="Monday" to="ArcGIS" />
          </span>
          <span className={styles.historyStatCount}>{crmCount}</span>
        </li>
      </ul>

      {hasErrors && (
        <div className={styles.historyErrors}>
          <button
            type="button"
            className={styles.errorsToggle}
            onClick={() => setErrorsOpen((v) => !v)}
            aria-expanded={errorsOpen}
          >
            {errorsOpen ? 'הסתר' : 'הצג'} {run.error_count} שגיאות
          </button>
          {errorsOpen && (
            <ul className={styles.errorsList}>
              {errors.map((msg, i) => (
                <li key={`${run.id}-err-${i}`}>{msg}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </article>
  );
}

export function SyncControlPanel({
  layerId,
  features,
  mapLoading = false,
}: SyncControlPanelProps) {
  const { status, history, refetch, isLoading } = useSyncStatus(layerId);
  const layerLabel = getLayerDisplayName(layerId);
  const syncHints = LAYER_SYNC_HINTS[layerId];
  const historyRef = useRef(history);
  historyRef.current = history;
  const pollTimersRef = useRef<ReturnType<typeof setInterval>[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [intervalMin, setIntervalMin] = useState<number>(5);
  const [targetUrl, setTargetUrl] = useState<string>('');
  const [boardId, setBoardId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (status) {
      setIntervalMin(Math.round(status.sync_interval_ms / 60_000));
      setTargetUrl(status.target_url ?? '');
      setBoardId(status.board_id ?? '');
    } else if (!isLoading) {
      setTargetUrl(syncHints?.suggestedTargetUrl ?? '');
      setBoardId('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.sync_interval_ms, status?.target_url, status?.board_id, isLoading, layerId]);

  useEffect(() => {
    return () => {
      for (const id of pollTimersRef.current) {
        clearInterval(id);
      }
      pollTimersRef.current = [];
    };
  }, []);

  useEffect(() => {
    return subscribeArcGisOAuthResult((msg) => {
      setSettingsOpen(true);
      if (msg.status === 'success') {
        setSyncError(null);
        void refetch();
      } else {
        setSyncError('התחברות ArcGIS נכשלה — סגור את החלון ונסה שוב.');
      }
    });
  }, [refetch]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauth = params.get('oauth');
    if (!oauth) return;

    if (oauth === 'success') {
      setSyncError(null);
      setSettingsOpen(true);
      void refetch();
    } else if (oauth === 'error') {
      setSyncError('התחברות ArcGIS נכשלה.');
      setSettingsOpen(true);
    }

    params.delete('oauth');
    const qs = params.toString();
    const next = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.replaceState({}, '', next);
  }, [refetch]);

  useEffect(() => {
    if (!settingsOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSettingsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [settingsOpen]);

  const exportableCount = useMemo(() => countExportableFeatures(features), [features]);

  async function handleExportGeoJson() {
    setExportNotice(null);
    let exportFeatures = features;
    try {
      exportFeatures = await fetchFreshGeoLayerFeatures(layerId);
    } catch (err) {
      setSyncError((err as Error).message);
      return;
    }

    const { filenames, stats } = await exportFeaturesToGeoJsonFile(exportFeatures, {
      layerId,
      layerDisplayName: getLayerDisplayName(layerId),
    });

    if (stats.featureCount === 0) {
      setSyncError('אין פריטים עם מיקום לייצוא — ודא שהמפה נטענה.');
      return;
    }

    if (stats.skippedDrawnWithoutPolygon > 0) {
      setSyncError(
        `יוצאו ${stats.featureCount} פריטים (${stats.polygonCount + stats.multiPolygonCount} מצוירים, ${stats.pointCount} נקודות). ${stats.skippedDrawnWithoutPolygon} מצוירים ללא פוליגון — עדכן את גאומטריה מגרש ב-Monday.`,
      );
      return;
    }

    setSyncError(null);

    const polygonTotal = stats.polygonCount + stats.multiPolygonCount;
    if (filenames.length > 1 && polygonTotal > 0) {
      setExportNotice(
        `הורדתם 2 קבצים: parcels (${polygonTotal} מגרשים) ו-sites (${stats.pointCount} נקודות). ב-ArcGIS Online הוסיפו את שני הקבצים כשכבות נפרדות — קובץ מעורב מציג רק נקודות.`,
      );
      return;
    }

    if (polygonTotal > 0) {
      setExportNotice(`הורד קובץ parcels עם ${polygonTotal} מגרשים.`);
    } else {
      setExportNotice(`הורד קובץ עם ${stats.pointCount} נקודות.`);
    }
  }

  async function handleCreateConfig() {
    setCreateError(null);
    const trimmedBoard = boardId.trim();
    if (!trimmedBoard) {
      setCreateError('נא להזין מזהה לוח Monday');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`/api/sync/layers/${layerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          board_id: trimmedBoard,
          target_url: targetUrl.trim() || undefined,
          provider: 'arcgis',
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string | string[] };
        const msg = Array.isArray(body.message) ? body.message.join(', ') : body.message;
        setCreateError(msg ?? `שמירה נכשלה (${res.status})`);
        return;
      }
      await refetch();
      setSettingsOpen(true);
    } catch {
      setCreateError('שגיאת רשת — לא ניתן לשמור הגדרות');
    } finally {
      setSaving(false);
    }
  }

  if (!status && isLoading) return null;

  if (!status) {
    return (
      <section className={styles.root} dir="rtl" aria-label="בקרת סנכרון ArcGIS">
        <div className={styles.summary}>
          <div className={styles.summaryStart}>
            <span className={`${styles.statusDot} ${styles.tone_muted}`} aria-hidden />
            <div className={styles.summaryText}>
              <span className={styles.summaryTitle}>
                סנכרון ArcGIS — {layerLabel}
              </span>
              <span className={styles.summaryHint}>
                אין עדיין הגדרות לשכבה זו. צור רשומה במערכת ואז התחבר ל-ArcGIS.
              </span>
            </div>
          </div>
          <div className={styles.summaryActions}>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => setSettingsOpen(true)}
            >
              הגדר סנכרון
            </button>
          </div>
        </div>

        {createError && (
          <p className={styles.bannerError} role="alert">
            {createError}
          </p>
        )}

        {settingsOpen &&
          createPortal(
            <div
              className={styles.modalBackdrop}
              role="presentation"
              onClick={() => setSettingsOpen(false)}
            >
              <div
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="sync-onboard-title"
                dir="rtl"
                onClick={(e) => e.stopPropagation()}
              >
                <header className={styles.modalHeader}>
                  <button
                    type="button"
                    className={styles.modalClose}
                    onClick={() => setSettingsOpen(false)}
                    aria-label="סגור"
                  >
                    ×
                  </button>
                  <div className={styles.modalHeaderMain}>
                    <div className={styles.modalHeaderText}>
                      <h2 id="sync-onboard-title" className={styles.modalTitle}>
                        הגדרת סנכרון — {layerLabel}
                      </h2>
                      <p className={styles.cardHint}>
                        נשמר בבסיס הנתונים (שכבה: <code dir="ltr">{layerId}</code>). אחרי
                        השמירה אפשר להתחבר ל-ArcGIS.
                      </p>
                    </div>
                  </div>
                </header>

                <div className={styles.modalBody}>
                  <div className={styles.card}>
                    <h3 className={styles.cardTitle}>Monday</h3>
                    <label className={styles.field}>
                      <span className={styles.fieldLabel}>מזהה לוח (board_id)</span>
                      <input
                        className={styles.urlInput}
                        type="text"
                        inputMode="numeric"
                        placeholder={syncHints?.boardHint ?? '5097499192'}
                        value={boardId}
                        onChange={(e) => setBoardId(e.target.value)}
                        disabled={saving}
                        dir="ltr"
                        spellCheck={false}
                      />
                    </label>
                    {syncHints?.boardHint && (
                      <p className={styles.cardHint}>{syncHints.boardHint}</p>
                    )}
                  </div>

                  <div className={styles.card}>
                    <h3 className={styles.cardTitle}>ArcGIS FeatureServer</h3>
                    <p className={styles.cardHint}>
                      כתובת שכבת היעד לסנכרון (שורש FeatureServer, ללא מספר שכבה)
                    </p>
                    <label className={styles.field}>
                      <span className={styles.fieldLabel}>FeatureServer URL</span>
                      <input
                        className={styles.urlInput}
                        type="url"
                        placeholder="https://…/FeatureServer"
                        value={targetUrl}
                        onChange={(e) => setTargetUrl(e.target.value)}
                        disabled={saving}
                        dir="ltr"
                        spellCheck={false}
                      />
                    </label>
                  </div>
                </div>

                <footer className={styles.modalFooter}>
                  <PoweredByClicksMondayGis className={styles.modalPoweredBy} />
                  <div className={styles.modalFooterActions}>
                    <button
                      type="button"
                      className={styles.btnPrimary}
                      onClick={() => void handleCreateConfig()}
                      disabled={saving}
                    >
                      {saving && <span className={styles.spinner} aria-hidden />}
                      שמור והמשך
                    </button>
                    <button
                      type="button"
                      className={styles.btnGhost}
                      onClick={() => setSettingsOpen(false)}
                    >
                      סגור
                    </button>
                  </div>
                </footer>
              </div>
            </div>,
            document.body,
          )}
      </section>
    );
  }

  const statusInfo = computeStatus(status.is_enabled, status.arcgisConnected, status.lastRun);
  const canEnable = status.arcgisConnected === true;
  const canSyncNow = status.arcgisConnected === true && targetUrl.trim().length > 0;
  function clearSyncPollTimers() {
    for (const id of pollTimersRef.current) {
      clearInterval(id);
    }
    pollTimersRef.current = [];
  }

  async function handleToggle(enabled: boolean) {
    setSaving(true);
    try {
      await fetch(`/api/sync/layers/${layerId}/config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_enabled: enabled }),
      });
      refetch();
    } finally {
      setSaving(false);
    }
  }

  async function handleIntervalBlur() {
    const clamped = Math.max(1, Math.min(60, intervalMin));
    setIntervalMin(clamped);
    if (!status || clamped * 60_000 === status.sync_interval_ms) return;
    setSaving(true);
    try {
      await fetch(`/api/sync/layers/${layerId}/config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sync_interval_ms: clamped * 60_000 }),
      });
      refetch();
    } finally {
      setSaving(false);
    }
  }

  async function handleSyncNow() {
    clearSyncPollTimers();
    setSyncError(null);
    setSyncing(true);
    try {
      const res = await fetch(`/api/sync/layers/${layerId}/trigger`, { method: 'POST' });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string | string[] };
        const msg = Array.isArray(body.message) ? body.message.join(', ') : body.message;
        setSyncError(msg ?? `סנכרון נכשל (${res.status})`);
        setSyncing(false);
        return;
      }
    } catch {
      setSyncError('שגיאת רשת — לא ניתן להפעיל סנכרון');
      setSyncing(false);
      return;
    }

    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      refetch();
      const latest = historyRef.current;
      const done = latest.length > 0 && latest[0].run_completed_at;
      if (done || attempts >= 12) {
        clearSyncPollTimers();
        setSyncing(false);
        if (done && latest[0].error_count > 0) {
          setSettingsOpen(true);
        }
      }
    }, 5_000);
    pollTimersRef.current.push(poll);
  }

  function handleConnectArcGIS() {
    const match = window.location.pathname.match(/\/board-view\/([^/]+)/);
    startArcGisOAuth(layerId, match?.[1]);
  }

  async function handleTargetUrlBlur() {
    const url = targetUrl.trim();
    if (url === (status?.target_url ?? '')) return;
    setSaving(true);
    try {
      await fetch(`/api/sync/layers/${layerId}/config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_url: url }),
      });
      refetch();
    } finally {
      setSaving(false);
    }
  }

  async function handleDisconnect() {
    if (!confirm('התנתק מ-ArcGIS? הסנכרון האוטומטי ייעצר.')) return;
    await fetch(`/api/sync/layers/${layerId}/arcgis-connection`, { method: 'DELETE' });
    refetch();
  }

  return (
    <section className={styles.root} dir="rtl" aria-label="בקרת סנכרון ArcGIS">
      <div className={styles.summary}>
        <div className={styles.summaryStart}>
          <span
            className={`${styles.statusDot} ${styles[`tone_${statusInfo.tone}`]}`}
            aria-hidden
          />
          <div className={styles.summaryText}>
            <span className={styles.summaryTitle}>{statusInfo.title}</span>
            <span className={styles.summaryHint}>
              {syncing ? 'מריץ סנכרון…' : statusInfo.hint}
            </span>
          </div>
        </div>

        {status.lastRun && !syncing ? <LastRunMetrics run={status.lastRun} /> : null}

        <div className={styles.summaryActions}>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => void handleSyncNow()}
            disabled={syncing || !canSyncNow || saving}
          >
            {syncing && <span className={styles.spinner} aria-hidden />}
            סנכרן עכשיו
          </button>
          <button
            type="button"
            className={styles.btnGhost}
            onClick={() => void handleExportGeoJson()}
            disabled={mapLoading || exportableCount === 0}
            title="הורדת GeoJSON (קבצים נפרדים למגרשים ולנקודות — נדרש ל-ArcGIS Online)"
          >
            ייצוא GeoJSON
          </button>
          <button
            type="button"
            className={styles.btnGhost}
            onClick={() => setSettingsOpen(true)}
            aria-haspopup="dialog"
          >
            הגדרות והיסטוריה
          </button>
        </div>
      </div>

      {syncError && (
        <p className={styles.bannerError} role="alert">
          {syncError}
          {' '}
          <button
            type="button"
            className={styles.bannerLink}
            onClick={() => setSettingsOpen(true)}
          >
            פתח הגדרות
          </button>
        </p>
      )}

      {exportNotice && !syncError ? (
        <p className={styles.bannerInfo} role="status">
          {exportNotice}
        </p>
      ) : null}

      {settingsOpen &&
        createPortal(
          <div
            className={styles.modalBackdrop}
            role="presentation"
            onClick={() => setSettingsOpen(false)}
          >
            <div
              className={styles.modal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="sync-settings-title"
              dir="rtl"
              onClick={(e) => e.stopPropagation()}
            >
              <header className={styles.modalHeader}>
                <button
                  type="button"
                  className={styles.modalClose}
                  onClick={() => setSettingsOpen(false)}
                  aria-label="סגור"
                >
                  ×
                </button>
                <div className={styles.modalHeaderMain}>
                  <div className={styles.modalHeaderText}>
                    <h2 id="sync-settings-title" className={styles.modalTitle}>
                      סנכרון Monday ↔ ArcGIS — {layerLabel}
                    </h2>
                    <ol className={styles.syncSteps}>
                      <li className={styles.syncStep}>
                        <span className={styles.stepNum}>1</span>
                        <span className={styles.stepBody}>
                          <strong>
                            <SyncFlowLabel from="ArcGIS" to="Monday" />
                          </strong>
                          <span className={styles.stepDetail}>
                            עדכון גאומטריה מ-ArcGIS ל-Monday
                          </span>
                        </span>
                      </li>
                      <li className={styles.syncStep}>
                        <span className={styles.stepNum}>2</span>
                        <span className={styles.stepBody}>
                          <strong>
                            <SyncFlowLabel from="Monday" to="ArcGIS" />
                          </strong>
                          <span className={styles.stepDetail}>
                            עדכון שדות CRM מ-Monday ל-ArcGIS
                          </span>
                        </span>
                      </li>
                    </ol>
                  </div>
                </div>
              </header>

              <div className={styles.modalBody}>
                <div className={styles.grid}>
                  <div className={styles.card}>
                    <h3 className={styles.cardTitle}>חיבור ArcGIS</h3>
                    <p className={styles.cardHint}>
                      כתובת שכבת FeatureServer (שורש, ללא מספר שכבה)
                    </p>
                    <label className={styles.field}>
                      <span className={styles.fieldLabel}>FeatureServer URL</span>
                      <input
                        className={styles.urlInput}
                        type="url"
                        placeholder="https://…/FeatureServer"
                        value={targetUrl}
                        onChange={(e) => setTargetUrl(e.target.value)}
                        onBlur={() => void handleTargetUrlBlur()}
                        disabled={saving}
                        dir="ltr"
                        spellCheck={false}
                      />
                    </label>
                    {isEmbeddedFrame() && !status.arcgisConnected && (
                      <p className={styles.cardHint}>
                        ייפתח חלון התחברות ל-Esri; לאחר אישור החלון ייסגר ותישארו בלוח Monday.
                      </p>
                    )}
                    <div className={styles.cardActions}>
                      {status.provider === 'arcgis' && !status.arcgisConnected && (
                        <button
                          type="button"
                          className={styles.btnSecondary}
                          onClick={handleConnectArcGIS}
                        >
                          התחברות ל-ArcGIS
                        </button>
                      )}
                      {status.provider === 'arcgis' && status.arcgisConnected && (
                        <span className={styles.connectedTag}>מחובר</span>
                      )}
                      {status.arcgisConnected && (
                        <button
                          type="button"
                          className={styles.btnDangerGhost}
                          onClick={() => void handleDisconnect()}
                        >
                          התנתק
                        </button>
                      )}
                    </div>
                  </div>

                  <div className={styles.card}>
                    <h3 className={styles.cardTitle}>סנכרון אוטומטי</h3>
                    <p className={styles.cardHint}>
                      סנכרון מתוזמן רץ ברקע לפי המרווח שבחרת
                    </p>
                    <label className={styles.toggleRow}>
                      <span className={styles.toggle}>
                        <input
                          type="checkbox"
                          checked={status.is_enabled}
                          disabled={!canEnable || saving}
                          onChange={(e) => void handleToggle(e.target.checked)}
                        />
                        <span className={styles.toggleSlider} />
                      </span>
                      <span className={styles.toggleText}>הפעל סנכרון מתוזמן</span>
                    </label>
                    <label className={styles.intervalField}>
                      <span className={styles.fieldLabel}>מרווח (דקות)</span>
                      <input
                        className={styles.intervalInput}
                        type="number"
                        min={1}
                        max={60}
                        value={intervalMin}
                        onChange={(e) => setIntervalMin(Number(e.target.value))}
                        onBlur={() => void handleIntervalBlur()}
                        disabled={saving || !status.is_enabled}
                      />
                    </label>
                  </div>
                </div>

                <div className={styles.historySection}>
                  <div className={styles.historyHeader}>
                    <h3 className={styles.cardTitle}>היסטוריית הרצות</h3>
                    <p className={styles.cardHint}>
                      כל שורה = הרצה אחת. המספרים מציינים כמה פריטים עודכנו בכל כיוון.
                    </p>
                  </div>
                  {history.length === 0 ? (
                    <p className={styles.historyEmpty}>
                      אין הרצות עדיין — לחץ «סנכרן עכשיו» לבדיקה.
                    </p>
                  ) : (
                    <div className={styles.historyList}>
                      {history.map((run) => (
                        <HistoryRow key={run.id} run={run} />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <footer className={styles.modalFooter}>
                <PoweredByClicksMondayGis className={styles.modalPoweredBy} />
                <div className={styles.modalFooterActions}>
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    onClick={() => void handleSyncNow()}
                    disabled={syncing || !canSyncNow || saving}
                  >
                    {syncing && <span className={styles.spinner} aria-hidden />}
                    סנכרן עכשיו
                  </button>
                  <button
                    type="button"
                    className={styles.btnGhost}
                    onClick={() => setSettingsOpen(false)}
                  >
                    סגור
                  </button>
                </div>
              </footer>
            </div>
          </div>,
          document.body,
        )}
    </section>
  );
}
